import React, { useState } from 'react';
import {
  Box,
  TextField,
  Button,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  Paper,
  Chip,
} from '@mui/material';
import { DeviceHub, Business } from '@mui/icons-material';
import axios from 'axios';

function MACLookup() {
  const [macAddress, setMacAddress] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState(null);

  const handleLookup = async () => {
    if (!macAddress) {
      setError('Please enter a MAC address');
      return;
    }

    // Validate MAC address format
    const macRegex = /^([0-9A-Fa-f]{2}[:-]){5}([0-9A-Fa-f]{2})$/;
    const cleanMac = macAddress.replace(/[:-]/g, '').toUpperCase();
    
    if (!macRegex.test(macAddress) && cleanMac.length !== 12) {
      setError('Please enter a valid MAC address (e.g., 00:1B:44:11:3A:B7)');
      return;
    }

    setLoading(true);
    setError(null);
    setResult(null);

    try {
      // Extract OUI (first 6 characters)
      const oui = cleanMac.substring(0, 6);
      
      try {
        // Try macvendors.co API first
        const response = await axios.get(`https://api.macvendors.com/${macAddress}`);
        
        setResult({
          macAddress: macAddress,
          oui: oui,
          vendor: response.data || 'Unknown',
          type: 'Ethernet/WiFi Device',
          isLocal: false,
          isMulticast: false,
          timestamp: new Date().toLocaleString()
        });
      } catch {
        // Fallback to IEEE OUI database lookup
        try {
          const response = await axios.get(`https://api.macaddress.io/v1?apikey=at_demo&output=json&search=${oui}`);
          
          if (response.data && response.data.vendorDetails) {
            setResult({
              macAddress: macAddress,
              oui: oui,
              vendor: response.data.vendorDetails.companyName || 'Unknown',
              country: response.data.vendorDetails.countryCode || 'Unknown',
              type: response.data.vendorDetails.companyAddress || 'Network Device',
              isLocal: (parseInt(cleanMac.charAt(1), 16) & 2) !== 0,
              isMulticast: (parseInt(cleanMac.charAt(1), 16) & 1) !== 0,
              timestamp: new Date().toLocaleString()
            });
          } else {
            throw new Error('No vendor found');
          }
        } catch {
          // Final fallback with common vendor patterns
          const commonVendors = {
            '001B44': 'Apple Inc.',
            '00D0C9': 'Intel Corporation',
            '001E58': 'Cisco Systems',
            '00E04C': 'Realtek Semiconductor',
            '001CF0': 'Dell Inc.',
            '002722': 'Hewlett Packard',
            '0050F2': 'Microsoft Corporation',
            '001A92': 'ASUSTek Computer',
            '00261C': 'Belkin International',
            '001DD8': 'Netgear Inc.'
          };

          const vendor = commonVendors[oui] || 'Unknown Vendor';
          
          setResult({
            macAddress: macAddress,
            oui: oui,
            vendor: vendor,
            type: 'Network Device',
            isLocal: (parseInt(cleanMac.charAt(1), 16) & 2) !== 0,
            isMulticast: (parseInt(cleanMac.charAt(1), 16) & 1) !== 0,
            timestamp: new Date().toLocaleString(),
            note: 'Vendor information from local database'
          });
        }
      }
    } catch (err) {
      setError('Unable to lookup MAC address vendor information');
    } finally {
      setLoading(false);
    }
  };

  const formatMacAddress = (mac) => {
    return mac.replace(/(.{2})/g, '$1:').slice(0, -1);
  };

  return (
    <Box sx={{ mt: 2 }}>
      <Typography variant="h4" gutterBottom sx={{ fontWeight: 600, mb: 3 }}>
        MAC Address Lookup
      </Typography>
      <Card sx={{ 
        p: 0,
        borderRadius: 2,
        background: 'linear-gradient(135deg, rgba(220, 20, 60, 0.05) 0%, rgba(0, 0, 0, 0.02) 100%)',
      }}>
        <CardContent sx={{ p: 4 }}>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
            <TextField
              fullWidth
              label="MAC Address"
              variant="outlined"
              value={macAddress}
              onChange={(e) => setMacAddress(e.target.value)}
              placeholder="00:1B:44:11:3A:B7"
              helperText="Enter MAC address in format: XX:XX:XX:XX:XX:XX or XX-XX-XX-XX-XX-XX"
            />
            
            <Button
              variant="contained"
              color="primary"
              onClick={handleLookup}
              disabled={loading}
              sx={{ mt: 2 }}
            >
              {loading ? <CircularProgress size={24} /> : 'Lookup Vendor'}
            </Button>
            
            {error && (
              <Alert severity="error" sx={{ mt: 2 }}>
                {error}
              </Alert>
            )}

            {result && (
              <Card sx={{ 
                mt: 2, 
                p: 3, 
                bgcolor: 'background.paper',
                border: '1px solid',
                borderColor: 'success.main',
                borderRadius: 2
              }}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <DeviceHub sx={{ mr: 1, color: 'primary.main' }} />
                  <Typography variant="h6" sx={{ color: 'primary.main' }}>
                    MAC Address Information
                  </Typography>
                </Box>
                
                {result.note && (
                  <Alert severity="info" sx={{ mb: 2 }}>
                    {result.note}
                  </Alert>
                )}
                
                <TableContainer component={Paper} sx={{ bgcolor: 'background.default' }}>
                  <Table>
                    <TableBody>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>MAC Address</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{result.macAddress}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>OUI (Vendor ID)</TableCell>
                        <TableCell sx={{ fontFamily: 'monospace' }}>{result.oui}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Vendor/Manufacturer</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <Business sx={{ mr: 1, color: 'primary.main' }} />
                            {result.vendor}
                          </Box>
                        </TableCell>
                      </TableRow>
                      {result.country && (
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Country</TableCell>
                          <TableCell>{result.country}</TableCell>
                        </TableRow>
                      )}
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Device Type</TableCell>
                        <TableCell>{result.type}</TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Address Type</TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', gap: 1 }}>
                            <Chip 
                              label={result.isLocal ? 'Locally Administered' : 'Globally Unique'}
                              color={result.isLocal ? 'warning' : 'success'}
                              size="small"
                            />
                            <Chip 
                              label={result.isMulticast ? 'Multicast' : 'Unicast'}
                              color={result.isMulticast ? 'info' : 'default'}
                              size="small"
                            />
                          </Box>
                        </TableCell>
                      </TableRow>
                      <TableRow>
                        <TableCell sx={{ fontWeight: 600 }}>Lookup Time</TableCell>
                        <TableCell>{result.timestamp}</TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Card>
            )}
          </Box>
        </CardContent>
      </Card>
    </Box>
  );
}

export default MACLookup;
